"use client";

/**
 * Client-side Proof-of-Work helpers. Fetches a signed challenge from
 * /api/auth/pow-challenge and brute-forces a SHA-256 hash with N leading
 * hex zeros via crypto.subtle.
 *
 * Pure browser API — no third-party scripts.
 */

export interface PowChallenge {
  challenge: string;
  difficulty: number;
  expires: number;
}

export async function fetchPowChallenge(kind: "default" | "admin" = "default"): Promise<PowChallenge> {
  const qs = kind === "admin" ? "?kind=admin" : "";
  const res = await fetch(`/api/auth/pow-challenge${qs}`, { cache: "no-store" });
  if (!res.ok) throw new Error(`pow_challenge_failed:${res.status}`);
  return res.json();
}

function toHex(buf: ArrayBuffer): string {
  const arr = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < arr.length; i++) s += arr[i].toString(16).padStart(2, "0");
  return s;
}

/**
 * Brute-force a nonce so that sha256(challenge + nonce) starts with
 * `difficulty` hex zeros. Yields back to the event loop periodically so the
 * UI stays responsive.
 *
 * Performance: difficulty=3 → ~4k hashes (~0.5-1s);
 *              difficulty=4 → ~65k hashes (~2-6s).
 */
export async function solvePow(c: PowChallenge, onProgress?: (n: number) => void): Promise<string> {
  const prefix = "0".repeat(c.difficulty);
  const enc = new TextEncoder();
  let nonce = 0;
  while (true) {
    const buf = enc.encode(`${c.challenge}${nonce}`);
    const hashBuf = await crypto.subtle.digest("SHA-256", buf);
    const hex = toHex(hashBuf);
    if (hex.startsWith(prefix)) return String(nonce);
    nonce++;
    if (nonce % 200 === 0) {
      onProgress?.(nonce);
      // Yield to the event loop so React can re-render the progress UI.
      await new Promise((r) => setTimeout(r, 0));
    }
  }
}

export async function obtainPowProof(
  kind: "default" | "admin" = "default",
  onProgress?: (n: number) => void,
): Promise<{ challenge: string; nonce: string }> {
  const c = await fetchPowChallenge(kind);
  const nonce = await solvePow(c, onProgress);
  return { challenge: c.challenge, nonce };
}
